/**
 * Route paths used throughout the application
 */
export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  INVITE: '/invite',
  JOB_LINK: (orgId: string, jobId: string) => `job/${orgId}/${jobId}`,

  // Company creation routes
  CREATE_COMPANY_CHECK: '/create-company/check',
  CREATE_COMPANY: '/create-company',

  // Setup routes
  SETUP: '/setup',
  SETUP_BILLING: '/setup/billing',
  SETUP_PROFILE: '/setup/profile',
  SETUP_TEAM: '/setup/team',
  SETUP_COMPLETE: '/setup/complete',

  // Dashboard routes
  BOARD: '/board',
  BOARD_DASHBOARD: '/board/dashboard',
  BOARD_DASHBOARD_CREATE_JOB: '/board/dashboard/create-job',
  BOARD_ANALYTICS: '/board/analytics',
  BOARD_BILLING: '/board/billing',
  BOARD_TEMPLATES: '/board/templates',
  BOARD_TEAM: '/board/team',
  BOARD_TEAM_ADD_MEMBER: '/board/team/add-member',
  BOARD_NOTIFICATIONS: '/board/notifications',
  BOARD_SETTINGS: '/board/settings',
  BOARD_DASHBOARD_CREATE_JOB_ID: (jobId: string) =>
    `/board/dashboard/create-job/${jobId}`,
  // Job routes
  JOB: (id: string) => `/job/${id}`,
  JOB_APPLY: (id: string) => `/job/${id}/apply`,
  JOB_APPLICANTS: (jobId: string) => `/board/dashboard/job/${jobId}/applicants`,

  // Interview routes
  INTERVIEW: (applicationId: string) => `/interview/${applicationId}`,

  // Candidate auth routes
  CANDIDATE_SIGNIN: '/candidate-signin',
  CANDIDATE_SIGNUP: '/candidate-signup',
  CANDIDATE_FORGOT_PASSWORD: '/candidate-forgot-password',
  OAUTH_REDIRECT: '/redirect',
} as const;

/**
 * Setup status values
 */
export const SETUP_STATUS = {
  COMPANY_PROFILE: 'company-profile',
  USER_PROFILE: 'user-profile',
  INVITE_TEAM: 'invite-team',
  COMPLETE: 'complete',
} as const;

export type SetupStatus =
  | typeof SETUP_STATUS.COMPANY_PROFILE
  | typeof SETUP_STATUS.USER_PROFILE
  | typeof SETUP_STATUS.INVITE_TEAM
  | typeof SETUP_STATUS.COMPLETE;

/**
 * User roles
 */
export const USER_ROLES = {
  ROOT_USER: 'ROOT_USER',
  ADMIN: 'ADMIN',
  RECRUITER: 'RECRUITER',
  SALES: 'SALES',
  CANDIDATE: 'CANDIDATE',
} as const;

export type UserRole =
  | typeof USER_ROLES.ROOT_USER
  | typeof USER_ROLES.ADMIN
  | typeof USER_ROLES.RECRUITER
  | typeof USER_ROLES.SALES
  | typeof USER_ROLES.CANDIDATE;

/**
 * LocalStorage keys for candidate session management
 */
export const CANDIDATE_ROLE_KEY = 'wisedoc_user_role';
export const CANDIDATE_REDIRECT_KEY = 'wisedoc_candidate_redirect';

/**
 * LocalStorage key for persisting pending team invitation across navigation
 * (e.g., forgot-password flow). Cleared after successful acceptance.
 */
export const PENDING_INVITE_KEY = 'wisedoc_pending_invite';

/**
 * Member status values
 */
export const MEMBER_STATUS = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export type MemberStatus =
  | typeof MEMBER_STATUS.PENDING
  | typeof MEMBER_STATUS.ACCEPTED
  | typeof MEMBER_STATUS.ACTIVE
  | typeof MEMBER_STATUS.INACTIVE;

/**
 * Setup step numbers
 */
export const SETUP_STEPS = {
  COMPANY_PROFILE: 1,
  USER_PROFILE: 2,
  INVITE_TEAM: 3,
} as const;

export const QUERY_KEYS = {
  AUTH_USER: ['auth', 'currentUser'],
  ORGANIZATIONS: (email: string) => ['organizations', email],
  JOB_POSTING: (orgUuid: string, jobId: string) => [
    'jobPosting',
    orgUuid,
    jobId,
  ],
  JOB_POSTINGS: (orgUuid: string) => ['jobPostings', orgUuid],
  APPLICATIONS: (orgId: string, jobId: string) => [
    'applications',
    orgId,
    jobId,
  ],
  APPLICATION_DETAILS: (jobId: string, applicationId: string) => [
    'applicationDetails',
    jobId,
    applicationId,
  ],
  INTERVIEW_DETAILS: (applicationId: string) => [
    'interviewDetails',
    applicationId,
  ],
  ROLE_VERIFICATION_CALIBRATION: (jobId: string, applicationId: string) => [
    'roleVerificationCalibration',
    jobId,
    applicationId,
  ],
  ROLE_EVIDENCE_REPORT: (jobId: string, applicationId: string) => [
    'roleEvidenceReport',
    jobId,
    applicationId,
  ],
  WORKSPACE: (orgUuid: string, workspaceId: string) => [
    'workspace',
    orgUuid,
    workspaceId,
  ],
  WORKSPACES: (orgUuid: string) => ['workspaces', orgUuid],
  COMPANY: (orgUuid: string) => ['company', orgUuid],
  MEMBER: (orgUuid: string) => ['member', orgUuid],
  TEAM: (orgUuid: string) => ['team', orgUuid],
  HAS_SAVED_CARD: (orgUuid: string) => ['hasSavedCard', orgUuid],
  BILLING_STATUS: (orgUuid: string) => ['billingStatus', orgUuid],
  PAYMENT_METHODS: (customerId: string) => ['paymentMethods', customerId],
  CURRENT_MONTH_BILL: (orgUuid: string) => ['currentMonthBill', orgUuid],
  INVOICES: (customerId: string) => ['invoices', customerId],
  BILLING_CONTACT: (orgUuid: string, customerId: string) => [
    'billingContact',
    orgUuid,
    customerId,
  ],
  USER_PROFILE: (orgUuid: string) => ['userProfile', orgUuid],
  IN_APP_NOTIFICATIONS: (orgUuid: string) => ['inAppNotifications', orgUuid],
  ENTERPRISE_PRICE: (region: string) => ['enterprisePrice', region],
  PUBLIC_JOB_POSTING: (jobId: string) => ['publicJobPosting', jobId],
  SALES_DASHBOARD_ACCESS: ['salesDashboardAccess'],
  SALES_ORGANIZATIONS: ['salesOrganizations'],
  SALES_ORGANIZATION_JOBS: (orgId: string) => ['salesOrganizationJobs', orgId],
  SALES_PLG_MEMBERS: ['salesPlgMembers'],
  PUBLIC_CANDIDATE_REPORT: (candidateId: string) => [
    'publicCandidateReport',
    candidateId,
  ],
  DOCUMENT_PREVIEW_URL: (filePath: string, fileName?: string) => [
    'documentPreviewUrl',
    filePath,
    fileName || '',
  ],
  ENTERPRISE_HIRING_METRICS: (
    orgUuid: string,
    startDate: string,
    endDate: string,
  ) => ['enterpriseHiringMetrics', orgUuid, startDate, endDate],
} as const;
